import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProductGridSkeleton, EmptyState } from "@/components/States";
import { CATEGORIES, DISTRICTS, toNeDigits } from "@/lib/constants";

const Marketplace = () => {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [district, setDistrict] = useState(params.get("district") || "");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (district) p.set("district", district);
    if (minPrice) p.set("min_price", minPrice);
    if (maxPrice) p.set("max_price", maxPrice);
    api
      .get(`/products?${p.toString()}`)
      .then((res) => setProducts(res.data))
      .finally(() => setLoading(false));
  }, [q, category, district, minPrice, maxPrice]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, district]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const clearFilters = () => {
    setQ("");
    setCategory("");
    setDistrict("");
    setMinPrice("");
    setMaxPrice("");
    setParams({});
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#14251F] mb-4">{t("market.title")}</h1>

      <form onSubmit={onSearchSubmit} className="flex items-center gap-2 bg-white rounded-full p-1.5 border border-[#E5E7EB] mb-4">
        <Search className="w-5 h-5 text-[#9A9E97] ml-3 shrink-0" />
        <input
          data-testid="market-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("home.searchPlaceholder")}
          className="flex-1 bg-transparent outline-none text-[#14251F] placeholder:text-[#9A9E97] text-sm py-2"
        />
        <button data-testid="market-search-btn" type="submit" className="shrink-0 px-4 py-2 rounded-full bg-[#123A2C] text-white text-sm font-medium">
          {t("home.search")}
        </button>
      </form>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button
          data-testid="toggle-filters"
          onClick={() => setShowFilters((s) => !s)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E5E7EB] text-sm font-medium text-[#14251F]"
        >
          <SlidersHorizontal className="w-4 h-4" /> {t("market.filters")}
        </button>

        {/* Quick category chips */}
        <button
          data-testid="chip-all-categories"
          onClick={() => setCategory("")}
          className={`px-3 py-2 rounded-lg text-sm font-medium ${!category ? "bg-[#123A2C] text-white" : "bg-white border border-[#E5E7EB] text-[#6B7280]"}`}
        >
          {t("market.allCategories")}
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`chip-category-${c.key}`}
            onClick={() => setCategory(c.key)}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${category === c.key ? "bg-[#123A2C] text-white" : "bg-white border border-[#E5E7EB] text-[#6B7280]"}`}
          >
            {c[lang]}
          </button>
        ))}
        {(q || category || district || minPrice || maxPrice) && (
          <button data-testid="clear-filters" onClick={clearFilters} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-[#DC2626]">
            <X className="w-4 h-4" /> {t("market.clear")}
          </button>
        )}
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#6B7280] mb-1 block">{t("market.district")}</label>
            <select
              data-testid="filter-district"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm bg-white outline-none focus:border-[#123A2C]"
            >
              <option value="">{t("market.allDistricts")}</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6B7280] mb-1 block">{t("market.minPrice")}</label>
            <input
              data-testid="filter-min-price"
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#123A2C]"
            />
          </div>
          <div>
            <label className="text-xs text-[#6B7280] mb-1 block">{t("market.maxPrice")}</label>
            <div className="flex gap-2">
              <input
                data-testid="filter-max-price"
                type="number"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#123A2C]"
              />
              <button data-testid="apply-filters" onClick={fetchProducts} className="px-4 rounded-lg bg-[#123A2C] text-white text-sm font-medium">
                {t("market.apply")}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <ProductGridSkeleton />
      ) : products.length === 0 ? (
        <EmptyState message={t("empty.noProducts")} testId="market-empty" />
      ) : (
        <>
          <p className="text-sm text-[#6B7280] mb-3" data-testid="market-results-count">
            {toNeDigits(products.length, lang)} {t("market.resultsCount")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Marketplace;
